import {useNavigate} from "react-router";
import {
    AppAvatar,
    AppBox,
    AppButton,
    AppDateAndTime,
    AppGridBox,
    AppPaper,
    AppScorePill,
    AppText,
    AppTitle,
} from "../../../../commons/components";
import Pill from "../../../../commons/components/Pill";
import IconLive from "../../../../commons/components/icons/live";
import {IFootballMatchDetail, IMatchState} from "../../../../commons/models/football/interface/i-football-match";
import {useEffect, useState} from "react";
import {MatchStateEnum} from "../../../../commons/enums/match-state-enum";
import moment from "moment";
import {MatchStatusEnum} from "../../../../commons/enums/match-status-enum";
import {formatLiveMatchTIme, getGameTime, getMatchPauseString} from "../../../../commons/utilities/game-time-utils";
import {MatchStatus} from "../../../../commons/components/ScorePill";
import AppBorderBox from "../../../../commons/components/BorderBox";
import AppRoundedDivider from "../../../../commons/components/RoundedDivider";
import ability from "../../../../casl/Can";
import {RolePolicyActionEnum} from "../../../../commons/enums/role-policy-action.enum";
import {ManageFootballTeam} from "../../../../commons/models/football/admin/manage-football-team";
import ToBeDecided from "../../../../commons/components/ui-components/ToBeDecided";
import {IFootballTeam} from "../../../../commons/models/football/interface/i-football-team";

interface footballMatchProps {
    match: IFootballMatchDetail;
    withBorder?:boolean;
}

export default function ManageMatchCard({match, withBorder}: footballMatchProps) {
    const navigate = useNavigate();
    const [penaltyGoals, setPenaltyGoals] = useState<{ home?: number; away?: number }>();
    const [selectedMatchState, setSelectedMatchState] = useState<IMatchState>();
    const [gameTime, setGameTime] = useState("");
    const [matchStatus, setMatchStatus] = useState<MatchStatus>();
    const handleMatchDetailsClick = () => {
        if(match.competition.teamId){
            navigate(`/manage/teams/${match.competition.teamId}/hosted-tournaments/${match.competition.id}/seasons/${match.season.id}/matches/${match.id}`)
        }else {
            navigate(`/manage/tournaments/${match.competition.id}/seasons/${match.season.id}/matches/${match.id}/`);
        }
        window.scrollTo(0, 0);
    };

    const canReadHomeTeam = match.homeTeam && ability.can(RolePolicyActionEnum.READ, new ManageFootballTeam().create(match?.homeTeam||{}) as ManageFootballTeam);
    const canReadAwayTeam = match.awayTeam && ability.can(RolePolicyActionEnum.READ, new ManageFootballTeam().create(match?.awayTeam||{}) as ManageFootballTeam);

    const handleTeamClick = (e: any,team:IFootballTeam|null, canRead: boolean|null) => {
        let path = `/manage/teams/${team?.id}`
        if(team?.parentId){
            path = `/manage/teams/${team.parentId}/sub-teams/${team.id}`;
        }
        if (!canRead) {
            e.stopPropagation();
            e.preventDefault();
        }
        navigate(path);
        window.scrollTo(0, 0);
    };

    useEffect(() => {
        switch (match.status) {
            case MatchStatusEnum.Upcoming:
                setMatchStatus("upcoming");
                break;
            case MatchStatusEnum.Live:
                setMatchStatus("live");
                break;
            case MatchStatusEnum.Finished:
                setMatchStatus("live");
                break;
        }
        if (match.status === MatchStatusEnum.Live) {
            const matchState = match.states.find((matchState) => matchState.state === MatchStateEnum.InProgress);
            if (matchState) {
                setSelectedMatchState(matchState);
            } else {
                const finishedMatchStates = match.states.filter((matchState) => matchState.state === MatchStateEnum.Completed);
                finishedMatchStates.sort((a, b) => moment(b.startAt).valueOf() - moment(a.startAt).valueOf());
                setSelectedMatchState(finishedMatchStates[0]);
            }
        }
        if (match.penalties.length > 0) {
            const homePenaltyGoal = match.penalties.filter(
                (penalty) => penalty.teamId === match.homeTeam?.id && penalty.goalScored
            ).length;
            const awayPenaltyGoal = match.penalties.filter(
                (penalty) => penalty.teamId === match.awayTeam?.id && penalty.goalScored
            ).length;
            setPenaltyGoals({home: homePenaltyGoal, away: awayPenaltyGoal});
        }
    }, [match]);

    useEffect(() => {
        if (match.status === MatchStatusEnum.Live && selectedMatchState) {
            const intervalId = setInterval(() => {
                setGameTime(getGameTime(selectedMatchState, match.gameplayDuration));
            }, 1000);
            return () => clearInterval(intervalId);
        }
    }, [match.status, selectedMatchState]);

    return (
        <AppPaper className="match-card" shadow={withBorder?"none":"xs"} padding="xs" withBorder={withBorder} >
            <AppBox flexDirection="column" gap="2xs">
                <AppBorderBox border={["Bottom"]} pb="xs">
                    <AppGridBox className="match-card-grid" gap="sm">
                        <AppGridBox className="align-center live-left-section" gap="xs">
                            <AppBox gap="xs" alignItems="center" justifyContent="end"
                                    style={{cursor:"pointer",pointerEvents: canReadHomeTeam ? "auto" : 'none'}}
                                    onClick={(e) => handleTeamClick(e, match.homeTeam, canReadHomeTeam)}>
                                {match.homeTeam ? (
                                    <>
                                        <AppTitle truncate={true} as="h6" textAlign="right">
                                            {match.homeTeam?.shortName}
                                        </AppTitle>
                                        <AppAvatar username={match.homeTeam?.name || ""}
                                                   src={match.homeTeam?.logo || ""}/>
                                    </>
                                ) : <ToBeDecided avatarSize={'md'} textAs={'h6'}/>}

                            </AppBox>
                            <AppBox flexDirection="column" alignItems="center" gap="2xs">
                                {matchStatus && (
                                    <AppScorePill
                                        matchStatus={matchStatus}
                                        valueFirst={match?.homeGoals || 0}
                                        valueSecond={match?.awayGoals || 0}
                                        penaltyValueFirst={penaltyGoals?.home}
                                        penaltyValueSecond={penaltyGoals?.away}
                                    />
                                )}
                                {match.status === MatchStatusEnum.Live && selectedMatchState && (
                                    <Pill
                                        icon={<IconLive/>}
                                        label={`live | ${
                                            selectedMatchState.state === MatchStateEnum.Completed
                                                ? getMatchPauseString(selectedMatchState, match.competition)
                                                : formatLiveMatchTIme(getGameTime(selectedMatchState, match.gameplayDuration))
                                        }`}
                                    ></Pill>
                                )}
                            </AppBox>
                            <AppBox gap="xs" alignItems="center" justifyContent="start"
                                    style={{cursor:"pointer",pointerEvents: canReadAwayTeam ? "auto" : 'none'}}
                                    onClick={(e) => handleTeamClick(e, match.awayTeam, canReadAwayTeam)}>
                                {match.awayTeam ? (
                                    <>
                                        <AppAvatar username={match.awayTeam?.name || ""}
                                                   src={match.awayTeam?.logo || ""}/>
                                        <AppTitle truncate={true} as="h6">
                                            {match.awayTeam?.shortName}
                                        </AppTitle>
                                    </>
                                ) : <ToBeDecided avatarSize={'md'} textAs={'h6'}/>}
                            </AppBox>
                        </AppGridBox>
                        <AppBox gap="xs" justifyContent="end" alignItems="center">
                            <AppButton size="lg" variant="light" label="Match Details"
                                       onClick={() => handleMatchDetailsClick()}/>
                        </AppBox>
                    </AppGridBox>
                </AppBorderBox>
                <AppBox justifyContent="center" alignItems="center">
                    <AppDateAndTime date={moment(match?.scheduledAt)} format="DD MMM, yyyy"/>
                    <AppRoundedDivider mx="2xs"/>
                    <AppDateAndTime date={moment(match?.scheduledAt)} format="HH:mm"/>
                    {match.stadium && (
                        <>
                            <AppRoundedDivider mx="2xs"/>
                            <AppText lineClamp={1}>
                                {match.stadium?.name}, {match.stadium?.city}
                            </AppText>
                        </>
                    )}
                </AppBox>
            </AppBox>
        </AppPaper>
    );
}
